import { useState } from 'react'

export type ValidationFunction<T> = (value: T) => T | null

export const useLocalStorage = <T>(
  key: string,
  defaultValue: T,
  validate?: ValidationFunction<T>, // Validate the stored value and return a replacement value if necessary.
): [T, React.Dispatch<React.SetStateAction<T>>] => {
  const [value, setValueWithoutLocalStorage] = useState<T>(() => {
    const storedValue = localStorage.getItem(key)

    if (storedValue) {
      const parsedValue = JSON.parse(storedValue)

      // If validation function is provided, use it to validate stored value
      if (validate) {
        const validatedValue = validate(parsedValue)

        if (validatedValue !== null) {
          return validatedValue
        }
      }
      
      return parsedValue
    }

    // If validation fails or no stored value exists, return default
    return defaultValue
  })

  const setValue = (newValue: React.SetStateAction<T>) => {
    const valueToStore = newValue instanceof Function ? newValue(value) : newValue

    localStorage.setItem(key, JSON.stringify(valueToStore))

    setValueWithoutLocalStorage(valueToStore)
  }

  return [value, setValue]
}
